import { Modification, ModificationAction } from '@core/types';
import { escapeHtml } from '@shared/utils/dom';
import { sanitizeText } from '@shared/utils/security';

export class PanelTemplate {
  static createHTML(): string {
    return `
      <header>
        <div>
          <svg class="ce-logo" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"></path>
          </svg>
          Campaign Editor <span class="ce-badge">LIVE</span>
        </div>
        <button class="ce-btn ce-btn-danger" data-ce="close">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
          Exit
        </button>
      </header>

      <div class="ce-body">
        <div class="ce-status-bar" data-ce="status">
          <div class="ce-status-indicator"></div>
          <span>Initializing...</span>
        </div>

        <div class="ce-section">
          <div class="ce-section-header">
            <svg class="ce-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
            <span>Current Page</span>
          </div>
          <div class="ce-page-path ce-mono" data-ce="path"></div>
        </div>

        <div class="ce-col" style="display: none;">
          <div class="ce-label">Selected element</div>
          <div class="ce-element-preview" data-ce="element-preview">
            <div class="ce-element-info">
              <div class="ce-element-type">📄 Page element</div>
              <div class="ce-element-name">Click on page to select</div>
            </div>
          </div>
          <input class="ce-input ce-mono" data-ce="selector" placeholder="Click any element on the page..." readonly />
          <div class="ce-selector-help">Click on any text, button, or element on the page to select it</div>
        </div>

        <div class="ce-section">
          <div class="ce-section-header">
            <svg class="ce-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="3"></circle>
              <path d="M12 1v6m0 6v6m4.22-13.22l4.24 4.24M1.54 8.96l4.24 4.24M20.46 8.96l-4.24 4.24"></path>
            </svg>
            <span>What do you want to change?</span>
          </div>
          <div class="ce-row">
            <div class="ce-col" style="flex:1">
              <select class="ce-select" data-ce="action">
                <option value="set_text">📝 Change text on page</option>
                <option value="hide">👁️ Hide something on page</option>
                <option value="replace_image">🖼️ Replace image</option>
              </select>
              <div class="ce-action-help">Choose what you want to do with the selected element</div>
            </div>
            <div class="ce-col" style="flex:1" data-ce-wrap="attr">
              <div class="ce-label" data-ce-label="attr">Attr name</div>
              <input class="ce-input ce-mono" data-ce="attr" value="href" />
            </div>
          </div>
        </div>

        <div class="ce-section" data-ce-wrap="value">
          <div class="ce-section-header">
            <svg class="ce-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
            <span data-ce-label="value">New text content</span>
          </div>
          <textarea class="ce-textarea ce-mono" data-ce="value" placeholder="Type the new text you want to appear on the page..."></textarea>
          <div class="ce-value-help">This text will replace the current text on the selected element</div>
        </div>

        <div class="ce-section" data-ce-wrap="image" style="display: none;">
          <div class="ce-section-header">
            <svg class="ce-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <circle cx="8.5" cy="8.5" r="1.5"></circle>
              <polyline points="21 15 16 10 5 21"></polyline>
            </svg>
            <span>Choose replacement image</span>
          </div>
          <div class="ce-image-upload">
            <input type="file" id="ce-image-input" accept="image/jpeg,image/jpg,image/png" style="display: none;">
            <div class="ce-upload-area" data-ce="upload-area">
              <div class="ce-upload-content">
                <svg class="ce-upload-icon" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                <div class="ce-upload-text">
                  <div class="ce-upload-title">Click to upload or drag and drop</div>
                  <div class="ce-upload-subtitle">PNG, JPG, JPEG up to 10MB</div>
                </div>
              </div>
            </div>
            <div class="ce-image-preview" data-ce="image-preview" style="display: none;">
              <img data-ce="preview-img" src="" alt="Preview" style="max-width: 100%; max-height: 200px; border-radius: 8px;">
              <div class="ce-image-info">
                <div class="ce-image-name" data-ce="image-name"></div>
                <button class="ce-btn ce-btn-small ce-btn-danger" data-ce="remove-image">Remove</button>
              </div>
            </div>
            <div class="ce-upload-status" data-ce="upload-status" style="display: none;">
              <div class="ce-upload-progress">
                <div class="ce-progress-bar"></div>
              </div>
              <div class="ce-upload-message">Uploading...</div>
            </div>
          </div>
        </div>

        <div class="ce-row">
          <button class="ce-btn ce-btn-primary" data-ce="add">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Add Modification
          </button>
        </div>

        <div class="ce-divider"></div>

        <div class="ce-section">
          <div class="ce-section-header">
            <svg class="ce-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 11H3v2h6v-2zm0-4H3v2h6V7zm0 8H3v2h6v-2zm12-8h-6v2h6V7zm0 4h-6v2h6v-2zm0 4h-6v2h6v-2z"></path>
            </svg>
            <span>Modifications for <span class="ce-mono" data-ce="path2"></span></span>
          </div>
          <div class="ce-list" data-ce="list"></div>
        </div>

        <div class="ce-row">
          <button class="ce-btn ce-btn-primary" data-ce="save">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"></path>
              <polyline points="17 21 17 13 7 13 7 21"></polyline>
              <polyline points="7 3 7 8 15 8"></polyline>
            </svg>
            Save Changes
          </button>
          <button class="ce-btn" data-ce="clear">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"></path>
            </svg>
            Clear All
          </button>
        </div>

        <div class="ce-divider"></div>

        <div class="ce-footer">
          <button class="ce-btn" data-ce="preview" id="preview-toggle">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
              <circle cx="12" cy="12" r="3"></circle>
            </svg>
            <span id="preview-text">Preview On</span>
          </button>
          <div class="ce-help ce-mono" data-ce="version"></div>
        </div>
      </div>
    `;
  }

  static renderModificationItem(
    mod: Modification,
    index: number,
    getActionDisplayName: (action: ModificationAction) => string,
  ): string {
    let val = '';

    if (mod.value != null) {
      if (mod.action === ModificationAction.REPLACE_IMAGE) {
        val = `<div class="ce-mini" style="margin-top:6px; opacity:.85">📷 Image file</div>`;
      } else {
        val = `<div class="ce-mini ce-mono" style="margin-top:6px; white-space:pre-wrap; word-break:break-word; opacity:.85">${escapeHtml(String(mod.value).slice(0, 250))}</div>`;
      }
    }

    const safeActionName = sanitizeText(getActionDisplayName(mod.action));
    const safeIndex = index + 1;

    return `
      <div class="ce-item" data-ce-mod-item="${index}">
        <div class="ce-item-header">
          <div class="ce-item-info">
            <div class="ce-mini">#${safeIndex} — ${safeActionName}</div>
            ${val}
          </div>
          <div class="ce-item-actions">
            <button class="ce-mini-btn danger" data-ce-item="remove" data-index="${index}" title="Remove modification">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"></path>
              </svg>
            </button>
          </div>
        </div>
        <div class="ce-item-actions">
          <button class="ce-mini-btn" data-ce-item="up" data-index="${index}">↑</button>
          <button class="ce-mini-btn" data-ce-item="down" data-index="${index}">↓</button>
        </div>
      </div>
    `;
  }
}
